import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { members, adminTeam } from '@discovr/api';
import type { GrantLevel } from '@discovr/api';
import { adminRoleLabel } from '@discovr/common';
import { campaignListAllOptions } from '@discovr/features';
import { Loader2 } from '../../icons';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { DIALOG_ELEVATION_CLASS } from '../ui/dialogElevation';
import { LoadingSpinner } from './LoadingSpinner';
import { CampaignAccessPicker } from './CampaignAccessPicker';
import type { CampaignAccessSelection } from './CampaignAccessPicker';
import { EmailRoleRow } from './EmailRoleRow';
import { AllCampaignsAccessPreview } from './AllCampaignsAccessPreview';

// Floor for the campaign section, applied from the very first render in
// EITHER role — not just once member-tier content has been measured — so a
// drawer opened cold on an owner-tier member doesn't render short before the
// actor ever switches roles. Both routine states now use the same h-80 box
// (AllCampaignsAccessPreview / CampaignAccessPicker's own selected list),
// so this just needs to clear the taller one's total — the search row above
// member-tier's box, or owner-tier's intro line (which can wrap to two) —
// so switching roles holds one steady dialog height instead of resizing;
// genuinely taller content (the demotion-review banner text) still grows
// past it. Action buttons live in the dialog's own footer, not in here.
const MIN_CAMPAIGN_SECTION_HEIGHT = 380;

// Roles assignable via the header's role Select — 'brand_owner'/'super_admin'
// are never offered here (ownership/root-admin isn't a plain role change).
const BRAND_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'brand_admin', label: 'Admin' },
  { value: 'brand_member', label: 'Member' },
];
const ADMIN_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'admin_member', label: 'Staff' },
];

// The owner-tier / member-tier role assignable per lane via this drawer's
// Select — used to detect a demotion (owner-tier → member-tier) so we can
// prompt a campaign-access review. Not the same as brand_owner/super_admin,
// which this drawer never offers as a target role.
const OWNER_TIER_ROLE: Record<'admin' | 'brand', string> = { admin: 'admin', brand: 'brand_admin' };
const MEMBER_TIER_ROLE: Record<'admin' | 'brand', string> = { admin: 'admin_member', brand: 'brand_member' };

const brandRoleLabel = (role: string): string =>
  role === 'brand_owner' ? 'Owner' : role === 'brand_admin' ? 'Admin' : 'Member';

export interface MemberAccessDrawerProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  member: { id: string; name: string; email: string; role: string; status: string };
  lane: 'brand' | 'admin';
  /** Undefined ⇒ caller may not change this member's role (rendered as static text). */
  onRoleChangeRequested?: (toRole: string) => void;
  /** True while the caller's own role-change mutation (triggered by
   *  `onRoleChangeRequested`) is in flight — disables the Select and the
   *  Cancel/Confirm footer buttons so a slow request can't be double-fired. */
  isRoleChangePending?: boolean;
  /** Overrides the lane's default role options (BRAND_ROLE_OPTIONS /
   *  ADMIN_ROLE_OPTIONS) — e.g. hiding a promote-only role from an actor who
   *  isn't allowed to grant it. Defaults to the full lane-based list. */
  roleOptions?: { value: string; label: string }[];
}

/**
 * MemberAccessDrawer — a centered modal showing one team member's
 * per-campaign access, opened from a Team-settings row in either app. For a
 * member-tier target, CampaignAccessPicker (search + a fixed-height
 * selected-campaigns box) IS the view — pre-seeded with their current
 * grants — rather than a separate read-only list gated behind an "Add
 * campaigns" step; a single "Grant access" saves the diff against the
 * previous grants (Finding 2) so only genuinely new/changed/removed entries
 * hit `updateAccess`. Owner-tier instead sees the "every campaign" notice.
 * Role changes stage in the header Select and only apply once the footer's
 * "Confirm role change" is clicked — Admin sees every campaign, so this is
 * deliberately not a single click.
 */
export function MemberAccessDrawer({
  open,
  onOpenChange,
  member,
  lane,
  onRoleChangeRequested,
  isRoleChangePending = false,
  roleOptions: roleOptionsProp,
}: MemberAccessDrawerProps) {
  const queryClient = useQueryClient();
  const api = lane === 'admin' ? adminTeam : members;
  const accessKey = ['member-access', lane, member.id] as const;

  const accessQuery = useQuery({
    queryKey: accessKey,
    queryFn: () => api.getAccess(member.id),
    enabled: open,
  });

  const [selection, setSelection] = React.useState<CampaignAccessSelection[]>([]);
  const [dialogContentEl, setDialogContentEl] = React.useState<HTMLDivElement | null>(null);
  const [pendingRole, setPendingRole] = React.useState<string | null>(null);
  const [pendingDemotionReview, setPendingDemotionReview] = React.useState(false);
  const [isDemotionReview, setIsDemotionReview] = React.useState(false);
  const [campaignSectionHeight, setCampaignSectionHeight] = React.useState<number>();
  const campaignSectionRef = React.useRef<HTMLDivElement>(null);
  // seededForRef backs the seeding effect below; prevRoleRef backs the
  // demotion-detection effect below it. Both are reset, alongside the state
  // above, by the "reset on reopen" logic further down.
  const seededForRef = React.useRef<string | null>(null);
  const prevRoleRef = React.useRef(member.role);

  // Both call sites now keep this component mounted across an open/close
  // cycle (rather than fully unmounting on close) so the dialog's own exit
  // transition — and the shared backdrop's — actually get to play instead
  // of the whole subtree vanishing mid-animation. That means a fresh open no
  // longer gets a clean slate for free from a remount, so this puts every
  // piece of per-session local state back to its opening default the moment
  // `open` flips true. State is adjusted directly during render — see
  // ActionConfirmDialog/SharePopover for the same pattern in this codebase
  // — but seededForRef/prevRoleRef are refs, which React doesn't allow
  // mutating during render, so those two go through a companion effect
  // instead; it's declared first, ahead of every effect that reads them, so
  // a fresh open's first commit already sees them cleared. Deliberately
  // keyed on `open` alone — a mid-session member.role change (e.g. this
  // same demotion flow landing) must reach the demotion-detection effect
  // below with prevRoleRef intact, not reset out from under it.
  const [prevOpen, setPrevOpen] = React.useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setSelection([]);
      setPendingRole(null);
      setPendingDemotionReview(false);
      setIsDemotionReview(false);
      setCampaignSectionHeight(undefined);
    }
  }
  React.useEffect(() => {
    if (open) {
      seededForRef.current = null;
      prevRoleRef.current = member.role;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Seeds `selection` from the member's real grants exactly once per
  // open/member — not on every refetch, which would otherwise clobber
  // in-progress local edits (including a pending Grant-access diff) the
  // moment e.g. a background refetch resolves. Also un-marks itself while
  // owner-tier (`all_campaigns`) so a promote-then-demote inside one open
  // dialog re-seeds from the fresh (possibly different) dormant grants
  // instead of resurrecting a stale local edit from before the promotion —
  // otherwise Grant/Confirm access would diff that stale selection against
  // the real grants and actually save the phantom change.
  React.useEffect(() => {
    if (!open) return;
    if (!accessQuery.data) return;
    if (accessQuery.data.all_campaigns) { seededForRef.current = null; return; }
    if (seededForRef.current === member.id) return;
    seededForRef.current = member.id;
    setSelection(accessQuery.data.grants.map((g) => ({ campaignId: g.campaign_id, campaignName: g.campaign_name, level: g.level })));
  }, [open, accessQuery.data, member.id]);

  const hasPendingRoleChange = pendingRole !== null && pendingRole !== member.role;
  // While a role change is staged, preview what confirming it would mean —
  // Admin/admin grants every campaign — instead of leaving the body showing
  // the pre-change state. Once nothing's staged, trust the server's own
  // `all_campaigns` (it also covers brand_owner/super_admin, which aren't
  // in OWNER_TIER_ROLE since this drawer never offers them as a target role).
  const showAllCampaignsPreview = hasPendingRoleChange
    ? pendingRole === OWNER_TIER_ROLE[lane]
    : !!accessQuery.data?.all_campaigns;

  // Demoting from owner-tier (brand_admin/admin) back to member-tier
  // (brand_member/admin_member) never clears campaign_grants server-side —
  // the member's pre-promotion grants are preserved dormant and silently
  // reappear. Rather than let that happen invisibly, detect the transition
  // and flag a review: the editor (always visible for member-tier) gets a
  // banner plus a Skip/Confirm pair in place of the routine Grant-access
  // button, so the actor explicitly confirms or adjusts those now-revisible
  // grants instead of them just reappearing with no signal anything changed.
  React.useEffect(() => {
    const prevRole = prevRoleRef.current;
    prevRoleRef.current = member.role;
    if (prevRole === OWNER_TIER_ROLE[lane] && member.role === MEMBER_TIER_ROLE[lane]) {
      setPendingDemotionReview(true);
    }
  }, [member.role, lane]);
  // Once the demotion's role-change has landed and the access query has
  // refetched — confirmed by real grants instead of `all_campaigns: true` —
  // flag the review. The seeding effect above (defined earlier, so it runs
  // first on the same commit) has by then already populated `selection`
  // from those grants. This genuinely needs to be an effect — it's
  // synchronizing with the access query (an external system) settling
  // asynchronously, not deriving from a prop available during this render.
  React.useEffect(() => {
    if (pendingDemotionReview && accessQuery.data && !accessQuery.data.all_campaigns && !accessQuery.isFetching) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reacts to accessQuery (react-query cache) resolving, not to a same-render prop
      setPendingDemotionReview(false);
      setIsDemotionReview(true);
    }
  }, [pendingDemotionReview, accessQuery.data, accessQuery.isFetching]);

  // Owner-tier's "Admins have access to every campaign" notice is much
  // shorter than the member-tier editor, so switching roles used to visibly
  // resize the dialog. MIN_CAMPAIGN_SECTION_HEIGHT floors both states from
  // the first paint; this additionally measures the editor so an unusually
  // tall one (many grants) raises owner-tier's floor to match, instead of
  // capping it at the static minimum.
  React.useEffect(() => {
    if (showAllCampaignsPreview || typeof ResizeObserver === 'undefined') return;
    const el = campaignSectionRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setCampaignSectionHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showAllCampaignsPreview]);

  const isMemberTier = !!accessQuery.data && !accessQuery.data.all_campaigns;
  // Needed in both states now — member-tier's editable picker AND
  // owner-tier's read-only "every campaign" preview.
  const campaignsQuery = useQuery({ ...campaignListAllOptions(), enabled: open });
  const campaigns = (campaignsQuery.data?.rows ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    brandName: c.brand_name,
  }));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: accessKey });
  const onAccessError = (e: Error) => toast.error(e.message || 'Failed to update campaign access.');

  const updateAccess = useMutation({
    mutationFn: (body: { upsert?: { campaign_id: string; level: GrantLevel }[]; revoke?: string[] }) =>
      api.updateAccess(member.id, body),
    onSuccess: () => { invalidate(); setIsDemotionReview(false); },
    onError: onAccessError,
  });

  const grants = accessQuery.data && !accessQuery.data.all_campaigns ? accessQuery.data.grants : [];

  const grantAccess = () => {
    // Only send upsert entries for selections that are genuinely new or
    // whose level actually changed — sending the whole selection makes the
    // backend record an activity/notification/email line for every entry,
    // even ones that didn't change (Finding 2).
    const previous = new Map(grants.map((g) => [g.campaign_id, g.level]));
    const keptIds = new Set(selection.map((s) => s.campaignId));
    const upsert = selection
      .filter((s) => previous.get(s.campaignId) !== s.level)
      .map((s) => ({ campaign_id: s.campaignId, level: s.level }));
    const revoke = [...previous.keys()].filter((id) => !keptIds.has(id));
    updateAccess.mutate({
      upsert: upsert.length > 0 ? upsert : undefined,
      revoke: revoke.length > 0 ? revoke : undefined,
    });
  };

  // Dismisses the demotion-review framing without saving — the editor stays
  // open with the same pre-filled selection either way, so nothing is lost;
  // the actor can still adjust and Grant access normally afterward.
  const skipReview = () => setIsDemotionReview(false);

  const roleOptions = roleOptionsProp ?? (lane === 'admin' ? ADMIN_ROLE_OPTIONS : BRAND_ROLE_OPTIONS);
  const roleLabel = lane === 'admin' ? adminRoleLabel(member.role) : brandRoleLabel(member.role);

  const footerContent = hasPendingRoleChange ? (
    <>
      <Button variant="outline" onClick={() => setPendingRole(null)} disabled={isRoleChangePending}>
        Cancel
      </Button>
      <Button onClick={() => onRoleChangeRequested?.(pendingRole)} disabled={isRoleChangePending}>
        {isRoleChangePending ? <Loader2 size={16} className="animate-spin" /> : 'Confirm role change'}
      </Button>
    </>
  ) : isMemberTier ? (
    isDemotionReview ? (
      <>
        <Button variant="outline" onClick={skipReview} disabled={updateAccess.isPending}>
          Skip for now
        </Button>
        <Button onClick={grantAccess} disabled={updateAccess.isPending}>
          {updateAccess.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Confirm access'}
        </Button>
      </>
    ) : (
      <Button onClick={grantAccess} disabled={updateAccess.isPending}>
        {updateAccess.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Grant access'}
      </Button>
    )
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-xl ${DIALOG_ELEVATION_CLASS}`} ref={setDialogContentEl}>
        <DialogHeader>
          <DialogTitle>Manage access</DialogTitle>
          <DialogDescription className="truncate">
            Update {member.name || member.email}&apos;s role and campaign access.
          </DialogDescription>
        </DialogHeader>

        <EmailRoleRow
          emailSlot={
            <p className="flex h-9 items-center truncate text-body-sm-regular text-foreground">{member.email}</p>
          }
          roleSlot={
            onRoleChangeRequested ? (
              <Select
                value={pendingRole ?? member.role}
                onValueChange={setPendingRole}
                disabled={updateAccess.isPending || isRoleChangePending}
              >
                <SelectTrigger className="w-full" aria-label={`Role for ${member.name || member.email}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="inline-flex h-9 items-center rounded-full border border-border bg-muted px-2.5 text-caption-medium text-foreground">
                {roleLabel}
              </span>
            )
          }
        />

        <div
          ref={campaignSectionRef}
          style={{ minHeight: Math.max(MIN_CAMPAIGN_SECTION_HEIGHT, campaignSectionHeight ?? 0) }}
        >
          {accessQuery.isLoading ? (
            <LoadingSpinner size={24} className="text-muted-foreground" />
          ) : accessQuery.isError ? (
            <div className="rounded-lg border border-rose-100 bg-card p-6 text-center">
              <p className="mb-3 text-body-sm-regular text-rose-600">Couldn&apos;t load campaign access.</p>
              <Button type="button" variant="outline" onClick={() => void accessQuery.refetch()}>
                Retry
              </Button>
            </div>
          ) : showAllCampaignsPreview ? (
            <div className="flex flex-col gap-3">
              <p className="text-body-sm-regular text-muted-foreground">
                Admins have access to every campaign — full privileges, so grant this role carefully.
              </p>
              <AllCampaignsAccessPreview campaigns={campaigns} isLoading={campaignsQuery.isLoading} />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {isDemotionReview && (
                <p className="text-body-sm-regular text-muted-foreground">
                  {member.name} previously had access to these campaigns — confirm or adjust before continuing.
                </p>
              )}
              <CampaignAccessPicker
                campaigns={campaigns}
                value={selection}
                onChange={setSelection}
                isLoading={campaignsQuery.isLoading}
                portalContainer={dialogContentEl}
              />
            </div>
          )}
        </div>

        {footerContent && <DialogFooter>{footerContent}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
